import { useGetPlatformPrograms } from "./../../Programs/programQueries";
import { useUser } from "../../User/userQueries";
import { addDays } from "date-fns";
import { format } from "date-fns/esm";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { Filter } from "../../../components/FiltersBar/FiltersBar.types";
import { useGetOrganizationPrograms } from "../../Programs/programQueries";
import { IProgram } from "../../Programs/ProgramTypes";
import { SESSION_STATUS } from "../../Sessions/SessionsTypes";
import { useUserCompany } from "../../User/userQueries";
import { useGetUsers } from "../analyticsReportQueries";
import { IUserAnalytics, SessionsReportsQuery } from "../analyticsReportsTypes";
import { useGetOrganizations } from "../../Company/companyQueries";
import { IOrganization } from "../../User/UserTypes";

/**
 * Depending on the query, we will return the related filters.
 */
interface ISessionFilterProps {
  query: SessionsReportsQuery;
  onQueryChange: Dispatch<SetStateAction<SessionsReportsQuery>>;
}

export const useSessionsFilters = ({ query, onQueryChange }: ISessionFilterProps) => {
  const { data: user } = useUser();
  const { data: userOrganization } = useUserCompany({ enabled: user?.account_type === "Hr" });
  const { data: organizations } = useGetOrganizations({ enabled: user?.account_type === "Admin" });
  const { data: userPrograms } = useGetOrganizationPrograms(
    {
      organizationId: userOrganization?._id
    },
    { enabled: user?.account_type === "Hr" }
  );
  const { data: programs } = useGetPlatformPrograms({}, { enabled: user?.account_type === "Admin" });

  const resources = useMemo(() => {
    if (organizations) {
      return {
        departments: organizations.map((o) => o.departments).flat(),
        entities: organizations.map((o) => o.entities).flat(),
        management_levels: organizations.map((o) => o.management_levels).flat(),
        programs
      };
    }

    return {
      departments: userOrganization?.departments || [],
      entities: userOrganization?.entities || [],
      management_levels: userOrganization?.management_levels || [],
      programs: userPrograms
    };
  }, [userOrganization, organizations, programs, userPrograms]);

  const [userSearchValue, setUserSearchValue] = useState<string>("");
  const [expertSearchValue, setExpertSearchValue] = useState<string>("");

  const { data: userData } = useGetUsers(
    {
      userClass: ["user"],
      limit: 1000,
      search_by_email_or_name: userSearchValue
    },
    { keepPreviousData: true }
  );

  const { data: expertData } = useGetUsers(
    {
      userClass: ["expert"],
      limit: 1000,
      search_by_email_or_name: expertSearchValue
    },
    { keepPreviousData: true }
  );

  const filters = useMemo(() => {
    const f: Filter<keyof SessionsReportsQuery, any>[] = [];
    Object.keys(query).forEach((key) => {
      switch (key) {
        case "organization":
          if (!organizations) break;
          const organization: Filter<"organization", IOrganization> = {
            label: "Organizations",
            name: "organization",
            type: "default",
            options: {
              renderText: (o) => o.name,
              data: organizations,
              searchCriteria: (option, keyword) => {
                return option.name?.toLowerCase().indexOf(keyword) !== -1;
              },
              isOptionSelected: (o) => query.organization!.includes(o._id),
              onChange: (newValue) => {
                onOptionsChange({
                  newValue: [...query.organization!, newValue._id],
                  type: "organization"
                });
              },
              onOptionsClear: () => {
                onOptionsChange({
                  newValue: [],
                  type: "organization"
                });
              },
              onOptionRemove: (option) => {
                onOptionsChange({
                  newValue: query.organization!.filter((id) => id !== option._id),
                  type: "organization"
                });
              }
            }
          };
          f.push(organization);
          break;

        case "program":
          if (!resources) break;
          const program: Filter<"program", IProgram> = {
            label: "Program",
            name: "program",
            type: "default",
            options: {
              renderText: (o) => o.name,
              data: resources.programs || [],
              searchCriteria: (option, keyword) => {
                return option.name?.toLowerCase().indexOf(keyword) !== -1;
              },
              isOptionSelected: (o) => query.program!.includes(o._id),
              onChange: (newValue) => {
                onOptionsChange({
                  newValue: [...query.program!, newValue._id],
                  type: "program"
                });
              },
              onOptionsClear: () => {
                onOptionsChange({
                  newValue: [],
                  type: "program"
                });
              },
              onOptionRemove: (option) => {
                onOptionsChange({
                  newValue: query.program!.filter((id) => id !== option._id),
                  type: "program"
                });
              }
            }
          };
          f.push(program);
          break;

        case "user":
          if (!userData) break;
          const users: Filter<"user", IUserAnalytics> = {
            label: "User",
            name: "user",
            type: "default",
            options: {
              renderText: (o) => (o.user_first_name ? `${o.user_first_name} ${o.user_last_name}` : o.user_email),
              data: userData.pages.flatMap((p) => p.users),
              onSearchInputChange: (keyword) => setUserSearchValue(keyword),
              isOptionSelected: (o) => query.user!.includes(o.user_id),
              onChange: (newValue) => {
                onOptionsChange({
                  newValue: [...query.user!, newValue.user_id],
                  type: "user"
                });
              },
              onOptionsClear: () => {
                onOptionsChange({
                  newValue: [],
                  type: "user"
                });
              },
              onOptionRemove: (option) => {
                onOptionsChange({
                  newValue: query.user!.filter((id) => id !== option.user_id),
                  type: "user"
                });
              }
            }
          };
          f.push(users);
          break;

        case "expert":
          if (!expertData) break;
          const experts: Filter<"expert", IUserAnalytics> = {
            label: "Expert",
            name: "expert",
            type: "default",
            options: {
              renderText: (o) => (o.user_first_name ? `${o.user_first_name} ${o.user_last_name}` : o.user_email),
              data: expertData.pages.flatMap((p) => p.users),
              onSearchInputChange: (keyword) => setExpertSearchValue(keyword),
              isOptionSelected: (o) => query.expert!.includes(o.user_id),
              onChange: (newValue) => {
                onOptionsChange({
                  newValue: [...query.expert!, newValue.user_id],
                  type: "expert"
                });
              },
              onOptionsClear: () => {
                onOptionsChange({
                  newValue: [],
                  type: "expert"
                });
              },
              onOptionRemove: (option) => {
                onOptionsChange({
                  newValue: query.expert!.filter((id) => id !== option.user_id),
                  type: "expert"
                });
              }
            }
          };
          f.push(experts);
          break;

        case "status":
          const status: Filter<"status", SESSION_STATUS> = {
            label: "Status",
            name: "status",
            type: "default",
            options: {
              renderText: (o) => o,
              data: ["Cancelled", "Completed", "No Show", "Pending"],
              searchCriteria: (option, keyword) => {
                return option?.toLowerCase().indexOf(keyword) !== -1;
              },
              isOptionSelected: (o) => query.status!.includes(o),
              onChange: (newValue) => {
                onOptionsChange({
                  newValue: [...query.status!, newValue],
                  type: "status"
                });
              },
              onOptionsClear: () => {
                onOptionsChange({
                  newValue: ["Cancelled", "Completed", "No Show", "Pending"],
                  type: "status"
                });
              },
              onOptionRemove: (option) => {
                onOptionsChange({
                  newValue: query.status!.filter((id) => id !== option),
                  type: "status"
                });
              }
            }
          };
          f.push(status);
          break;
        case "department":
          if (!resources) return;
          const department: Filter<"department", string> = {
            label: "Department",
            name: "department",
            type: "default",
            options: {
              renderText: (o) => o,
              data: resources.departments,
              searchCriteria: (option, keyword) => {
                return option?.toLowerCase().indexOf(keyword) !== -1;
              },
              isOptionSelected: (o) => query.department!.includes(o),
              onChange: (newValue) => {
                onOptionsChange({
                  newValue: [...query.department!, newValue],
                  type: "department"
                });
              },
              onOptionsClear: () => {
                onOptionsChange({
                  newValue: [],
                  type: "department"
                });
              },
              onOptionRemove: (option) => {
                onOptionsChange({
                  newValue: query.department!.filter((id) => id !== option),
                  type: "department"
                });
              }
            }
          };
          f.push(department);
          break;
        case "entities":
          if (!resources) return;
          const entity: Filter<"entities", string> = {
            label: "Entity",
            type: "default",
            name: "entities",
            options: {
              renderText: (o) => o,
              data: resources.entities,
              searchCriteria: (option, keyword) => {
                return option?.toLowerCase().indexOf(keyword) !== -1;
              },
              isOptionSelected: (o) => query.entities!.includes(o),
              onChange: (newValue) => {
                onOptionsChange({
                  newValue: [...query.entities!, newValue],
                  type: "entities"
                });
              },
              onOptionsClear: () => {
                onOptionsChange({
                  newValue: [],
                  type: "entities"
                });
              },
              onOptionRemove: (option) => {
                onOptionsChange({
                  newValue: query.entities!.filter((id) => id !== option),
                  type: "entities"
                });
              }
            }
          };
          f.push(entity);
          break;
        case "management_level":
          if (!resources) return;
          const management_level: Filter<"management_level", string> = {
            label: "Management level",
            name: "management_level",
            type: "default",
            options: {
              renderText: (o) => o,
              data: resources.management_levels,
              searchCriteria: (option, keyword) => {
                return option?.toLowerCase().indexOf(keyword) !== -1;
              },
              isOptionSelected: (o) => query.management_level!.includes(o),
              onChange: (newValue) => {
                onOptionsChange({
                  newValue: [...query.management_level!, newValue],
                  type: "management_level"
                });
              },
              onOptionsClear: () => {
                onOptionsChange({
                  newValue: [],
                  type: "management_level"
                });
              },
              onOptionRemove: (option) => {
                onOptionsChange({
                  newValue: query.management_level!.filter((id) => id !== option),
                  type: "management_level"
                });
              }
            }
          };
          f.push(management_level);
          break;
        case "date":
          const dateFilter: Filter<"startDate", string> = {
            label:
              (query.date![0].startDate &&
                query.date![0].endDate &&
                `${format(query.date![0].startDate, "yyyy-MM-dd")} / ${format(query.date![0].endDate, "yyyy-MM-dd")}`) ||
              "Date",
            name: "startDate",
            type: "dateRange",
            options: {
              onChange: (newValue) => {
                onOptionsChange({
                  newValue,
                  type: "date"
                });
              },
              onOptionsClear: () => {
                onOptionsChange({
                  newValue: [
                    {
                      endDate: addDays(Date.now(), 7),
                      key: "selection"
                    }
                  ],
                  type: "date"
                });
              },
              ranges: query.date!
            }
          };
          f.push(dateFilter);
          break;
      }
    });

    return f;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userOrganization, programs, query, userData, expertData, userSearchValue]);

  const onOptionsChange = <T extends keyof SessionsReportsQuery>({ newValue, type }: { type: T; newValue: SessionsReportsQuery[T] }) => {
    onQueryChange((state) => ({ ...state, [type]: newValue }));
  };
  return filters;
};
